import { useState, forwardRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from "@mui/material/Container";

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    TextField,
    useMediaQuery
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

import SubmitButton from '../buttons/SubmitButton';
import CCSelect from '../dataEntry/CCSelect';
import GameModeSelect from '../dataEntry/GameModeSelect';
import GrandPrixEntryPlayer from '../dataEntry/GrandPrixEntryPlayer';
import GroupSizeSelect from '../dataEntry/GroupSizeSelect';
import RuleDisplay from '../dataDisplay/RuleDisplay';
import Header from "../headers/Header";
import { getAllowedGroupSizes, getRule } from "../utils/RuleLogic";
import { validateGrandPrixInput } from '../utils/InputValidation';
import {
    abandonGameSession,
    createGameSession,
    getActiveGameSession,
    GameSessionApiError,
} from '../utils/GameSessionApi';
import {
    createGameplayDraft,
    clearLegacyGameplayStorage,
    GAMEPLAY_SESSION_STORAGE_KEY,
    isGameplayDraftV1,
} from '../utils/GameplayDraft';
import LoadingShells from '../utilComponents/LoadingShells';
import { useRoster } from '../utils/useRoster';




const Constants = require("../constants/Constants");
let IMAGE_URL = Constants.IMAGE_URL;
let CCLIST = Constants.CCLIST;

const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const GrandPrix = () => {

    const isXs = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const theme = useTheme();
    const navigate = useNavigate();

    const gameModeInit = {
        "id": -1,
        "name": "select",
        "url": `${IMAGE_URL}game_modes/SelectMode.png`,
    }

    const alertInit = {
        "show": false,
        "messages": ["Missing place 2."]
    }

    const ccInit = {
        "id": 0,
        "name": "select",
        "url": `${IMAGE_URL}cc_icons/SelectCC.png`,
    }

    const ruleParameterInit = {
        "rule": "Undefined Rules",
        "ruleId": "6",
        "raceCount": 0,
        "groupSize": 0,
        "playerCount": 0
    }

    // Sorts CC list to display in drop down
    CCLIST.ccs.sort((a, b) => (a.id > b.id) ? 1 : -1)

    // ------ Variables / Set Functions ------

    // Setting Init Roster
    const { roster } = useRoster();

    const [gpRoster, setGpRoster] = useState([])

    const [alert, setAlert] = useState(alertInit)
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false)
    const [activeSession, setActiveSession] = useState(null)
    const [checkingActiveSession, setCheckingActiveSession] = useState(true)
    const [activeSessionCheckFailed, setActiveSessionCheckFailed] = useState(false)

    const [gpTitle, setGpTitle] = useState("");

    // Init Variable/Set Function for gameMode and CC toggle buttons 
    const [showCC, setShowCC] = useState(false);
    const [showGameMode, setShowGameMode] = useState(false);

    // Init Variable / Set Function for CC Data needed to display choice and submission to API
    const [ccData, setCCData] = useState(ccInit);
    const [gameMode, setGameMode] = useState(gameModeInit)


    const [ruleParameters, setRuleParameters] = useState(ruleParameterInit)

    const [groupSize, setGroupSize] = useState('-')


    // const windowSize = useRef([window.innerWidth, window.innerHeight]);
    // const maxWidth = windowSize.current[0] / 2;

    // Handler for CC List Toggle
    const toggleCC = (e) => {
        e?.preventDefault();
        setShowCC((showCC) => !showCC);
        console.log(showCC)
    };

    // Handler for CC List Toggle
    const toggleGameMode = (e) => {
        e?.preventDefault();
        setShowGameMode((showGameMode) => !showGameMode);
    };

    // ------ Handlers ------

    // handler for gp roster player select
    const handlePlayerSelect = (e) => {
        // Get the selected values from the multi-select
        const selectedValues = e.target.value;
        
        // If nothing is selected, clear the roster
        if (!selectedValues || selectedValues.length === 0) {
            setGpRoster([]);
            updateRule();
            return;
        }
        
        // Get the last selected value (the one that triggered the change)
        const lastSelectedValue = selectedValues[selectedValues.length - 1];
        const newPlayer = typeof lastSelectedValue === 'string' ? JSON.parse(lastSelectedValue) : lastSelectedValue;

        // Check if the player is already in the roster
        const playerIndex = gpRoster.findIndex(player => player.playerId === newPlayer.playerId);
        
        if (playerIndex >= 0) {
            // Player is already in the roster, remove them
            const updatedRoster = gpRoster.filter(player => player.playerId !== newPlayer.playerId);
            setGpRoster(updatedRoster);
        } else {
            // Add the new player if we haven't reached the limit
            if (gpRoster.length >= 8) {
                setAlert({ show: true, messages: ["Cannot add more than 8 players."] });
                return;
            }
            
            // Add the new player to the roster
            setGpRoster(prevRoster => [...prevRoster, newPlayer]);
        }
        
        updateRule();
    }


    // Handler for closing Data Entry Error Alert Dialog
    const handleAlertClose = () => {
        setAlert(alertInit);
    };

    // Handler for Successful Grand Prix Submission
    const handleSuccessClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setShowSuccess(false);
    };

    // Automatically close the success banner after 5 seconds
    useEffect(() => {
        if (showSuccess) {
            const timer = setTimeout(() => {
                setShowSuccess(false);
            }, 5000); // 5 seconds

            // Cleanup the timer if the component unmounts or success state changes
            return () => clearTimeout(timer);
        }
    }, [showSuccess]);

    // Handler for CC Choice from drop down
    const handleCC = (e) => {
        e.preventDefault();
        setCCData(
            {
                "id": e.target.getAttribute('value'),
                "name": e.target.getAttribute('name'),
                "url": e.target.getAttribute('src'),
            }
        );
        toggleCC()
    };

    // Handler for Game Mode Choice from drop down
    const handleGameMode = (e) => {
        e.preventDefault();
        console.log(parseInt(e.target.getAttribute('value')) === 1);
        setGameMode(
            {
                "id": e.target.getAttribute('value'),
                "name": e.target.getAttribute('name'),
                "url": e.target.getAttribute('src'),
            }
        );
        toggleGameMode();
    };

    const handleGroupSizeSelect = (e) => {
        console.log("Group Size Select: ", e.target.value)
        setGroupSize(e.target.value);
        updateRule();
    }

    const updateRule = () => {
        setRuleParameters(getRule(gpRoster.length, groupSize));
        // console.log("new rule params: ", ruleParameters);
    }

    // Handler for Title entry
    const updateTitle = (e) => {
        // const val = evt.target.value;
        // this.setState({
        //     gpTitle: val
        // });
        setGpTitle(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (checkingActiveSession || activeSessionCheckFailed || activeSession) {
            setAlert({
                show: true,
                messages: [checkingActiveSession
                    ? 'Wait while the active Grand Prix check finishes.'
                    : activeSessionCheckFailed
                        ? 'Retry the active Grand Prix check before starting another.'
                    : 'Resume or abandon the active Grand Prix before starting another.'],
            });
            return;
        }
        // Original race entry data
        let gpEntryDataToValidate = {
            "ruleId": ruleParameters.ruleId,
            "title": gpTitle,
            "hardcore": parseInt(gameMode.id),
            "groupSize": groupSize,
            "ccId": ccData.id
        };

        let validateData = validateGrandPrixInput(gpEntryDataToValidate);
        console.log("validateData: ", validateData);

        if (validateData.valid) {
            setLoading(true);
            try {
                const draft = createGameplayDraft({
                    title: gpTitle,
                    ruleInfo: ruleParameters,
                    hardcore: parseInt(gameMode.id) === 1,
                    engineClassId: ccData.id,
                    roster: gpRoster,
                    savedRoster: roster,
                });
                const session = await createGameSession(draft);
                if (!isGameplayDraftV1(session.draftState)) {
                    throw new Error('The active gameplay session uses an unsupported format.');
                }
                clearLegacyGameplayStorage();
                localStorage.setItem(GAMEPLAY_SESSION_STORAGE_KEY, session.sessionId);
                setCCData(ccInit);
                setShowSuccess(true);
                navigate(`/grandprix/${session.sessionId}`);
            } catch (error) {
                const message = error instanceof GameSessionApiError
                    ? error.message
                    : 'Unable to start the Grand Prix session.';
                setAlert({ show: true, messages: [message] });
            } finally {
                setLoading(false);
            }

        } else {
            console.log(validateData.messages);
            setAlert({ "show": true, "messages": validateData.messages });
            setLoading(false);

        }
    };

    const handleAbandonActiveSession = async () => {
        setLoading(true);
        try {
            await abandonGameSession(activeSession.sessionId, activeSession.version);
            clearLegacyGameplayStorage();
            localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
            setActiveSession(null);
        } catch (error) {
            if (error instanceof GameSessionApiError && error.status === 409) {
                try {
                    const latest = await getActiveGameSession();
                    setActiveSession(latest);
                    setAlert({
                        show: true,
                        messages: [
                            'This Grand Prix changed in another window and was not abandoned. Review the latest state, then try again.',
                        ],
                    });
                } catch (reloadError) {
                    if (reloadError instanceof GameSessionApiError && reloadError.status === 404) {
                        localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
                        setActiveSession(null);
                        setAlert({
                            show: true,
                            messages: ['This Grand Prix is no longer active.'],
                        });
                    } else {
                        setAlert({
                            show: true,
                            messages: [reloadError.message || 'Unable to reload the active Grand Prix.'],
                        });
                    }
                }
                return;
            }
            setAlert({
                show: true,
                messages: [error.message || 'Unable to abandon the active Grand Prix.'],
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        getActiveGameSession()
            .then((session) => {
                if (!cancelled) {
                    setActiveSession(session);
                }
            })
            .catch((error) => {
                if (!(error instanceof GameSessionApiError && error.status === 404)) {
                    console.error('Unable to check for an active gameplay session:', error);
                    if (!cancelled) setActiveSessionCheckFailed(true);
                }
            })
            .finally(() => {
                if (!cancelled) setCheckingActiveSession(false);
            });
        return () => { cancelled = true; };
    }, []);



    useEffect(() => {
        if (!getAllowedGroupSizes(gpRoster.length).includes(groupSize)) {
            setGroupSize('-');
            setRuleParameters(getRule(gpRoster.length, '-'));
            return;
        }
        setRuleParameters(getRule(gpRoster.length, groupSize));
    }, [gpRoster, groupSize])


    return (
        <Container
            component="main"
            sx={{
                marginTop: 4,
                flexDirection: "column",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
            {loading ? (
                <LoadingShells />
            ) : (<>
                <Snackbar open={showSuccess} autoHideDuration={6000} onClose={handleSuccessClose}>
                    <Alert onClose={handleSuccessClose} severity="success" sx={{ width: '100%' }}>
                        Grand Prix Successfully Started!
                    </Alert>
                </Snackbar>
                <Dialog
                    open={alert.show}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        {"Data Entry Errors"}
                    </DialogTitle>
                    <DialogContent>
                        {alert.messages?.map((message, index) =>
                            <DialogContentText key={`error-message-${index}`} id={`error-message-${index}`}>
                                {index + 1}. {message}
                            </DialogContentText>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleAlertClose} autoFocus>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ p: 1, mb: 3 }}
                >
                    <Header src={`${IMAGE_URL}headers/GrandPrix.png`} alt="Grand Prix Header" />
                </Box>

                {activeSession && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <Alert severity="info" sx={{ mb: 1 }}>
                            {isGameplayDraftV1(activeSession.draftState)
                                ? 'An active Grand Prix must be resumed or abandoned before starting another.'
                                : 'An older active draft cannot be resumed by this version. Abandon it to start again.'}
                        </Alert>
                        {isGameplayDraftV1(activeSession.draftState) && (
                            <Button
                                variant="contained"
                                sx={{ mr: 1 }}
                                onClick={() => {
                                    clearLegacyGameplayStorage();
                                    localStorage.setItem(GAMEPLAY_SESSION_STORAGE_KEY, activeSession.sessionId);
                                    navigate(`/grandprix/${activeSession.sessionId}`);
                                }}
                            >
                                Resume {activeSession.draftState.setup.title}
                            </Button>
                        )}
                        <Button variant="outlined" color="error" onClick={handleAbandonActiveSession}>
                            Abandon Draft
                        </Button>
                    </Box>
                )}

                {activeSessionCheckFailed && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <Alert severity="error" sx={{ mb: 1 }}>
                            The active Grand Prix check failed. Starting another session is disabled.
                        </Alert>
                        <Button variant="outlined" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </Box>
                )}

                {!activeSession && !activeSessionCheckFailed && <Box component="form" onSubmit={handleSubmit} noValidate id="GrandPrixEntryForm"
                    sx={{
                        flexDirection: "column",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "white",
                        borderRadius: 4,
                        border: 2
                    }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="grandprixtitle"
                        label="Grand Prix Title"
                        name="grand prix title"
                        autoComplete="grand prix title"
                        autoFocus
                        onChange={updateTitle}
                        sx={{ width: '90%' }}
                    />
                    <RuleDisplay
                        ruleParameters={ruleParameters}
                        key={toString(groupSize) + ruleParameters.ruleId} />

                    <CCSelect
                        showCC={showCC}
                        handleCC={handleCC}
                        ccData={ccData}
                        toggleCC={toggleCC}
                        headerWidth={isXs ? '60%' : "80%"}
                    />
                    <GameModeSelect
                        showGameMode={showGameMode}
                        handleGameMode={handleGameMode}
                        gameMode={gameMode}
                        toggleGameMode={toggleGameMode}
                        headerWidth={isXs ? '50%' : "70%"}
                    />
                    <GroupSizeSelect
                        handleGroupSize={handleGroupSizeSelect}
                        playerCount={gpRoster.length}
                        groupSize={groupSize}
                    />
                    <GrandPrixEntryPlayer
                        id={"gp-select"}
                        gpRoster={gpRoster}
                        handlePlayerSelect={handlePlayerSelect}
                        roster={roster}
                        theme={theme}
                    />
                    <SubmitButton />
                </Box>}
            </>)}
        </Container>
    );
};

export default GrandPrix;
